import { defineStore } from 'pinia'
import { MenuQueryDocument } from '~/api/graphql/generated'
import { LanguageId, MenuQueryQuery, MenuQueryQueryVariables } from '~/api/graphql/generated'
import { LanguageCode } from '~/composables/useLocale'
import { useTypedApolloClients } from '~/lib/utils/graphqlDocument'

export type MenuName = 'main' | 'meta-menu' | 'footer' | 'compliance-menu'

// MenuLink should come from Drupal GraphQL types, but defining locally for now
export interface MenuLink {
  entity?: {
    __typename?: string
    fieldIsShopPage?: boolean
    fieldShopCategory?: string
    langcode?: {
      value: string
    }
  }
  url?: {
    path?: string
  }
  [key: string]: unknown
}

export type ElementToFilter = MenuLink & { links: ElementToFilter[] | null }

const filterLinksByLanguage = (element: ElementToFilter, language: LanguageCode): ElementToFilter => {
  const filteredElement: ElementToFilter = {
    ...element,
    links: [...(element?.links || [])]
      ?.map((link: ElementToFilter) => filterLinksByLanguage(link, language))
      .filter((link: ElementToFilter) => link.entity?.langcode?.value === language)
      .map((link: ElementToFilter) => {
        if (link.entity?.__typename === 'MenuLinkContentMain' && link.entity?.fieldIsShopPage && link.url) {
          return {
            ...link,
            url: {
              path: link.entity.fieldShopCategory,
            },
          }
        }
        return link
      }),
  }

  return filteredElement
}

interface MenuData {
  [key: string]: ElementToFilter | null
}

export const useMenuStore = defineStore('menuStore', () => {
  const clients = useTypedApolloClients()
  const { languageCode } = useLocale()

  const menuData = ref<MenuData>({})
  const previewLanguage = ref<LanguageCode | null>(null)

  const getMenuKey = (menuName: MenuName, lang: LanguageCode) => `${menuName}-${lang}`

  const fetchMenu = async (menuName: MenuName, lang: LanguageCode, useCache = true) => {
    const key = getMenuKey(menuName, lang)

    if (useCache && menuData.value[key]) {
      return menuData.value[key]
    }

    try {
      const variables: MenuQueryQueryVariables = { menu: menuName, language: lang.toUpperCase() as LanguageId }
      const { data } = await clients!.default.query<MenuQueryQuery, MenuQueryQueryVariables>({
        query: MenuQueryDocument,
        variables,
        fetchPolicy: useCache ? 'cache-first' : 'no-cache',
      })

      if (!data.menuByName) {
        menuData.value[key] = { links: [] }
        return menuData.value[key]
      }

      const filtered = filterLinksByLanguage(data.menuByName as ElementToFilter, lang)
      menuData.value[key] = filtered

      return filtered
    } catch (error) {
      console.error(`[MenuStore] Failed to fetch menu ${menuName} for language ${lang}:`, error)
      menuData.value[key] = { links: [] }
      return menuData.value[key]
    }
  }

  const getMenu = (menuName: MenuName, lang?: LanguageCode) => {
    return computed(() => {
      // Priority: explicit lang > preview language > current language code
      const targetLang = lang || previewLanguage.value || languageCode.value
      const key = getMenuKey(menuName, targetLang)
      return menuData.value[key] || null
    })
  }

  const refreshAllMenus = async (lang: LanguageCode) => {
    const menus: MenuName[] = ['main', 'meta-menu', 'footer', 'compliance-menu']

    await Promise.all(menus.map((menuName) => fetchMenu(menuName, lang, false)))
  }

  const setPreviewLanguage = async (lang: LanguageCode) => {
    previewLanguage.value = lang
    // Ensure menus are loaded for the preview language
    await refreshAllMenus(lang)
  }

  const clearPreviewLanguage = () => {
    previewLanguage.value = null
  }

  // Watch for language changes and refresh all menus
  watch(
    languageCode,
    async (newLang, oldLang) => {
      if (newLang !== oldLang) {
        await refreshAllMenus(newLang)
      }
    },
    { immediate: false },
  )

  return {
    fetchMenu,
    getMenu,
    refreshAllMenus,
    setPreviewLanguage,
    clearPreviewLanguage,
    menuData,
  }
})
